// ---------------------------------------------------------------------------
// Quality Control → Metric View deep-link (PRODOPSAI-648)
//
// Builds the `/metric-view` URL a Management View QC matrix cell click should
// navigate to: same team and the cell's exact period expressed as a Metric
// View custom date range (?from/?to). It always lands on the default Product
// Flow tab — the hygiene-drill sub-tabs were tried but the landing felt off,
// so every QC metric links to Product Flow (PRODOPSAI-648 follow-up). The tab
// param is omitted because Product Flow is Metric View's default tab.
//
// `includeArchived` is always set so an archived team selected here is not
// dropped by Metric View's "team must exist in the dropdown" guard.
// ---------------------------------------------------------------------------

/**
 * Subtract one day from an ISO `YYYY-MM-DD` date. The matrix period `end` is
 * exclusive (the next period's start); Metric View's `to` is inclusive, so a
 * "May" column (`end = 2026-06-01`) becomes `to = 2026-05-31`.
 */
export function isoMinusOneDay(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  return new Date(ms - 86_400_000).toISOString().slice(0, 10);
}

export interface IQcDeepLinkArgs {
  teamId: string;
  /** The clicked column's `[start, end)` period; `end` is exclusive. */
  period?: { start: string; end: string };
}

/**
 * Build the `/metric-view?…` deep-link for a clicked QC cell. Lands on Product
 * Flow (default tab) scoped to the team + period. When the period is missing
 * (older payloads without `columnPeriods`), the date range is omitted and
 * Metric View falls back to its default time pill.
 */
export function buildQcDeepLink({ teamId, period }: IQcDeepLinkArgs): string {
  const params = new URLSearchParams();
  params.set('teamId', teamId);
  // PRODOPSAI-648: always include archived teams so the deep-linked team
  // survives Metric View's dropdown guard even when it is archived.
  params.set('includeArchived', 'true');
  if (period) {
    params.set('from', period.start);
    params.set('to', isoMinusOneDay(period.end));
  }
  return `/metric-view?${params.toString()}`;
}
